/**
 * Journey Data Model
 * Defines the structure for personalization journeys
 */

/**
 * Create a product item for the journey
 * @param {string} name - Product name
 * @param {Array} components - Array of component definitions
 * @returns {Object} Product object
 */
export function createProduct(name, components = []) {
  return {
    name,
    components, // Component structure from JSON (not saved to Firebase)
    personalization: [], // User input data: [{ type: 'pictures', input: ['url1'], timestamp: 123 }]
    personalizationType: null, // 'custom' (user personalizes) or 'professional' (fast personalization)
    isComplete: false,
    createdAt: Date.now(),
    updatedAt: Date.now()
  };
}

/**
 * Create a personalization entry
 * @param {string} type - Type: 'pictures', 'text', 'color', 'selection', etc.
 * @param {Array|string} input - The input data (array of URLs for pictures, string for text/color)
 * @returns {Object} Personalization entry
 */
export function createPersonalizationEntry(type, input) {
  return {
    type,
    input,
    timestamp: Date.now()
  };
}

/**
 * Create a new journey
 * @param {string} journeyId - Unique journey identifier
 * @param {string} userId - User ID
 * @param {string} journeyType - Type of journey (e.g., 'set', 'single')
 * @param {string} setName - Name of the product set
 * @returns {Object} Journey object
 */
export function createJourney(journeyId, userId, journeyType, setName) {
  return {
    journeyId,
    userId,
    journeyType,
    setName,
    setProducts: [],
    status: 'IN_PROGRESS',
    isComplete: false, // Backward compatibility
    step: 0,
    profileType: null,
    petName: null,
    petType: null,
    instagram: null,
    createdAt: Date.now(),
    updatedAt: Date.now()
  };
}

/**
 * Add a product to the journey
 * @param {Object} journey - Journey object
 * @param {Object} product - Product item
 * @returns {Object} Updated journey
 */
export function addProductToJourney(journey, product) {
  return {
    ...journey,
    setProducts: [...journey.setProducts, product],
    updatedAt: Date.now()
  };
}

/**
 * Update a product in the journey
 * @param {Object} journey - Journey object
 * @param {number} productIndex - Index of the product to update
 * @param {Object} updates - Updates to apply
 * @returns {Object} Updated journey
 */
export function updateProductInJourney(journey, productIndex, updates) {
  const updatedProducts = [...journey.setProducts];
  updatedProducts[productIndex] = {
    ...updatedProducts[productIndex],
    ...updates,
    updatedAt: Date.now()
  };
  
  // Check if all products are complete
  const allComplete = updatedProducts.every(p => p.isComplete === true);
  
  return {
    ...journey,
    setProducts: updatedProducts,
    isComplete: allComplete,
    status: allComplete ? 'FINISHED' : 'IN_PROGRESS',
    updatedAt: Date.now()
  };
}

/**
 * Add personalization to a product
 * @param {Object} product - Product item
 * @param {string} type - Personalization type
 * @param {*} input - Input data
 * @returns {Object} Updated product
 */
export function addPersonalizationToProduct(product, type, input) {
  const entry = createPersonalizationEntry(type, input);
  return {
    ...product,
    personalization: [...product.personalization, entry],
    updatedAt: Date.now()
  };
}

/**
 * Update personalization in a product
 * @param {Object} product - Product item
 * @param {number} personalizationIndex - Index of personalization to update
 * @param {*} input - New input data
 * @returns {Object} Updated product
 */
export function updatePersonalizationInProduct(product, personalizationIndex, input) {
  const updatedPersonalization = [...product.personalization];
  updatedPersonalization[personalizationIndex] = {
    ...updatedPersonalization[personalizationIndex],
    input,
    timestamp: Date.now()
  };
  
  return {
    ...product,
    personalization: updatedPersonalization,
    updatedAt: Date.now()
  };
}

/**
 * Update journey metadata
 * @param {Object} journey - Journey object
 * @param {Object} metadata - Metadata to update (step, status, profileType, etc.)
 * @returns {Object} Updated journey
 */
export function updateJourneyMetadata(journey, metadata) {
  const updated = {
    ...journey,
    ...metadata,
    updatedAt: Date.now()
  };
  
  // Sync isComplete with status for backward compatibility
  if (metadata.status === 'FINISHED') {
    updated.isComplete = true;
  } else if (metadata.status === 'IN_PROGRESS') {
    updated.isComplete = false;
  }
  
  return updated;
}

/**
 * Mark journey as complete
 * @param {Object} journey - Journey object
 * @returns {Object} Updated journey
 */
export function completeJourney(journey) {
  // Update all products to be complete
  const updatedProducts = journey.setProducts ? journey.setProducts.map(p => ({
    ...p,
    isComplete: true
  })) : [];
  
  return {
    ...journey,
    setProducts: updatedProducts,
    status: 'FINISHED',
    isComplete: true,
    completedAt: Date.now(),
    updatedAt: Date.now()
  };
}

/**
 * Validate journey structure
 * @param {Object} journey - Journey object to validate
 * @returns {boolean} True if valid
 */
export function validateJourney(journey) {
  if (!journey || typeof journey !== 'object') return false;
  if (!journey.journeyId || !journey.userId) return false;
  if (!Array.isArray(journey.setProducts)) return false;
  return true;
}

/**
 * Clean journey for storage (remove non-serializable data)
 * @param {Object} journey - Journey object
 * @returns {Object} Cleaned journey
 */
export function cleanJourneyForStorage(journey) {
  const cleaned = { ...journey };
  
  // Clean products
  if (Array.isArray(cleaned.setProducts)) {
    cleaned.setProducts = cleaned.setProducts.map(product => {
      const cleanedProduct = {
        name: product.name || '',
        isComplete: product.isComplete || false,
        personalizationType: product.personalizationType || null, // 'custom' or 'professional'
        // USER DATA ONLY - Don't save components array (comes from JSON)
        personalization: [],
        updatedAt: product.updatedAt || Date.now()
      };
      
      // Only add optional fields if they have values
      if (product.showComponent !== undefined) {
        cleanedProduct.showComponent = product.showComponent;
      }
      if (product.professional !== undefined) {
        cleanedProduct.professional = product.professional;
      }
      if (product.createdAt) {
        cleanedProduct.createdAt = product.createdAt;
      }
      
      // Clean personalization entries - ensure image URLs are strings only (not File objects)
      if (Array.isArray(product.personalization)) {
        cleanedProduct.personalization = product.personalization.map(entry => {
          const cleanedEntry = {
            type: entry.type,
            timestamp: entry.timestamp || Date.now()
          };
          
          // For pictures, ensure we only store URLs (not File objects)
          if (entry.type === 'pictures' && Array.isArray(entry.input)) {
            cleanedEntry.input = entry.input.map(item => {
              if (typeof item === 'string') return item;
              if (item && item.url) return item.url;
              if (item && item.storageUrl) return item.storageUrl;
              return null;
            }).filter(Boolean);
          } else {
            cleanedEntry.input = entry.input;
          }
          
          return cleanedEntry;
        });
      }
      
      return cleanedProduct;
    });
  }
  
  // Remove any undefined values from the cleaned object
  return removeUndefinedValues(cleaned);
}

/**
 * Remove undefined values from object recursively
 * @param {Object} obj - Object to clean
 * @returns {Object} Cleaned object
 */
function removeUndefinedValues(obj) {
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedValues(item));
  }
  
  if (obj !== null && typeof obj === 'object') {
    const cleaned = {};
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      if (value !== undefined) {
        cleaned[key] = removeUndefinedValues(value);
      }
    });
    return cleaned;
  }
  
  return obj;
}
